import { useSelector, useDispatch } from "react-redux";
import { Polygon } from "@react-google-maps/api";
import { useState, useCallback, useRef, useEffect } from "react";
import { getTownsBoundaries, setCitiesUserCounts, setSelectedCity, setSelectedTown, setUserBoundariesList } from "../../features/boundaries/boundariesSlice";
import CustomInfoWindow from "./CustomInfoWindow";
import { setFilteredUsersLocations } from "../../features/locs/locsSlice";

// Polygon için seçenekler (hover, aktif vs.)
const options = {
  strokeColor: "#010691",
  strokeOpacity: 1,
  strokeWeight: 1,
  fillColor: "#010691",
  fillOpacity: 0.1,
  clickable: true,
  zIndex: 5,
};

const hoveredOptions = {
  strokeColor: "#010691",
  strokeOpacity: 1,
  strokeWeight: 1,
  fillColor: "#010691",
  fillOpacity: 0.4,
  clickable: true,
  zIndex: 5,
};

const activeOptions = {
  strokeColor: "#010691",
  strokeOpacity: 1,
  strokeWeight: 1,
  fillColor: "transparent",
  clickable: true,
  zIndex: 5,
};

const CitiesBoundaries = () => {
  const { citiesBoundariesList, selectedCity, selectedRegion, userBoundariesList, citiesUserCounts } = useSelector((state) => state.boundaries);
  const dispatch = useDispatch();
  const [hoveredCity, setHoveredCity] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);
  const workerRef = useRef()

  const {allUsersLocationList} = useSelector(state => state.locs)

  const handlePolygonMouseOver = useCallback((item, userCount, event) => {
    
    setHoveredCity({
      cityId: item.cityId,
      id: item.id,
      cityName: item.cityName,
      position: { lat: event.latLng.lat(), lng: event.latLng.lng() },
      userCount: userCount
    });
  }, []);

  // Mouse move ile info window konumunu güncelliyoruz.
  const handlePolygonMouseMove = useCallback((event) => {
    setHoveredCity((prev) =>
      prev ? { ...prev, position: { lat: event.latLng.lat(), lng: event.latLng.lng() } } : null
    );
  }, []);

  const handlePolygonMouseOut = useCallback(() => {
    setHoveredCity(null);
  }, []);




    useEffect(() => {
      
      // Worker oluşturuluyor.
      const worker = new Worker(new URL("../../workers/multiCityBoundariesFilterWorker.js", import.meta.url));
      workerRef.current = worker;
      
      worker.postMessage({
        markers:allUsersLocationList,
        polygons: citiesBoundariesList,
      });
      
      worker.onmessage = (e) => {
        // Eğer gelen markers dizisi boşsa veya beklenmeyen bir sonuçsa, işlemi göz ardı edebilirsiniz.
        if (!e.data || e.data.markers.length === 0 || e.data.polygons.length === 0) {
          // Boş sonuç: İstediğiniz gibi ignore edin.
          return;
        }
        console.log(e);
  
        dispatch(setCitiesUserCounts(e.data.polygons))
        dispatch(setUserBoundariesList({data: e.data.markers.map(item => {
          return {
            insideBoundaryCityId: item?.insideBoundary,
            id: item?.id
          }
        }),
        type: "city"
      }))
        
        worker.terminate();
        workerRef.current = null;
      };
  
      
      return () => {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      };
    }, [allUsersLocationList, citiesBoundariesList, dispatch]);


    const getDynamicWidthContent = useCallback(({cityName, userCount}) => {

      
      if(!cityName || userCount === null || userCount === undefined || userCount === false){
        return;
      }
      const text1 = cityName;
      const text2 = userCount?.toString();
  
    // Padding değerleri: sol, üst, sağ, alt
    const leftPadding = 12;
    const topPadding = 3;
    const rightPadding = 8;
    const bottomPadding = 8; // Alt padding değeri
    const lineHeight = 18; // Her satırın yüksekliği
  
    // İki satır için en geniş metni baz alarak genişliği hesaplıyoruz.
    const maxLength = Math.max(text1.length, text2.length);
    const width = maxLength * 8 + leftPadding + rightPadding;
    // Yükseklik: üst padding + iki satırın yüksekliği + alt padding
    const height = topPadding + lineHeight * 2 + bottomPadding;
  
      return (
        <div style={{ padding: 0, margin: 0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height}>
            <rect x="0" y="0" width={width} height={height} rx="8" ry="8" fill="black" />
            <text
              x={leftPadding}
              y={topPadding + lineHeight}
              fontSize="12"
              fill="white"
              fontFamily="Arial"
              textAnchor="start" // Sola yaslı
              fontVariant={"lining-nums"}
            >
            <tspan x={leftPadding} dy="0">{text1}</tspan>
            <tspan x={leftPadding} dy={lineHeight}>{text2} Üye</tspan>
            </text>
          </svg>
        </div>
      );
    }, []);
  

    const handleClick = useCallback(
      (item) => {
        dispatch(
          setSelectedCity({
            cityId: item.cityId,
            id: item.id,
            cityName: item.cityName,
            centerCoords: item.centerCoords,
            locatedRegionId: item.locatedRegionId,
          })
        );
        setHoveredCity(null);
        dispatch(setSelectedTown(null));
        dispatch(getTownsBoundaries({ cityId: item.cityId }));
  
        const insideUsersIds = userBoundariesList.filter(userItem => item.cityId === userItem.insideBoundaryCityId).map(userItem => userItem.id)
        
        dispatch(setFilteredUsersLocations({data: allUsersLocationList.filter(userItem => insideUsersIds.includes(userItem.id)), type: "filter"}))
      },
      [dispatch, allUsersLocationList, userBoundariesList]
    );


  if (!citiesBoundariesList || citiesBoundariesList.length < 1) return null;

  return (
    <>
      {citiesBoundariesList.map((item, i) => {
        // Eğer şehir zaten seçiliyse, bu şehir için polygon render edilmiyor.
        if (selectedCity?.cityId === item.cityId) return null;
        return item.coords.map((boundary, index) => {
          const isHovered = hoveredCity?.cityId === item.cityId;
          const isActive = selectedRegion?.regionId === item.locatedRegionId && !selectedCity?.cityId;
          const polygonOptions = isHovered ? hoveredOptions : isActive ? activeOptions : options;
          return (
            <Polygon
              key={`${item.cityId}-${index}`}
              path={boundary}
              options={polygonOptions}
              onMouseOver={(event) => handlePolygonMouseOver(item, citiesUserCounts[i]?.userCount, event)}
              onMouseMove={handlePolygonMouseMove}
              onMouseOut={handlePolygonMouseOut}
              onClick={() => handleClick(item)}
            />
          );
        });
      })}
      {hoveredCity?.position && hoveredCity && (
        <CustomInfoWindow
          position={hoveredCity.position}
          content={getDynamicWidthContent({cityName: hoveredCity.cityName, userCount: hoveredCity?.userCount})}

        />
      )}
    </>
  );
};

export default CitiesBoundaries;
